import React, { useState, useEffect, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  LabelList,
} from "recharts";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { BusinessAccountService } from "../api/generated/services/BusinessAccountService";
import { BalanceBracketMode } from "../api/generated/models/BalanceBracketMode";
import type { TopCustomerBracketViewModel } from "../api/generated/models/TopCustomerBracketViewModel";
import { formatInteger } from "../helpers/formatNumber";
import {
  dateToCopenhagenStartOfDayUTC,
  dateToCopenhagenEndOfDayUTC,
} from "../helpers/dateFilters";

const BAR_COLORS = [
  "#503595",
  "#7059ba",
  "#7e69ca",
  "#8d78d9",
  "#9b88e8",
  "#aa97f8",
  "#b8a8ff",
  "#c6bbff",
  "#aa97f8",
  "#8d78d9",
  "#7059ba",
];

const getDateRangeForThisMonth = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
};

const getDateRangeForPreviousMonth = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
};

const getDateRangeForLastThreeMonths = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
};

const formatBracketLabel = (bracket: TopCustomerBracketViewModel): string => {
  const from = (bracket.fromExclusive ?? 0) + 0.01;
  if (bracket.toInclusive == null) {
    return `${bracket.fromExclusive?.toLocaleString()}+ p`;
  }
  return `${from % 1 === 0 ? from.toLocaleString() : from.toFixed(2)} - ${bracket.toInclusive.toLocaleString()} p`;
};

const SHOW_MODE_FILTER = false;

const MODE_LABELS: Record<BalanceBracketMode, string> = {
  [BalanceBracketMode.ALL]: "All (Net Balance)",
  [BalanceBracketMode.ALL_EARNED_POINTS]: "All Earned Points",
  [BalanceBracketMode.ALL_DOWNSIZED_BILLS]: "All Downsized Bills",
};

const AdminCustomerBrackets: React.FC = () => {
  const [brackets, setBrackets] = useState<TopCustomerBracketViewModel[]>([]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [mode, setMode] = useState<BalanceBracketMode>(BalanceBracketMode.ALL_EARNED_POINTS);
  const [selectedQuickFilter, setSelectedQuickFilter] = useState<string>("allTime");
  // Date used by the CURRENT filter — net balance "as of" this date.
  // Defaults to today, which is equivalent to "right now".
  const [currentAsOfDate, setCurrentAsOfDate] = useState<string>(() =>
    new Date().toISOString().slice(0, 10)
  );

  const fetchBrackets = useCallback(async () => {
    try {
      setLoading(true);

      const from = startDate
        ? dateToCopenhagenStartOfDayUTC(startDate)
        : undefined;
      const to = endDate
        ? dateToCopenhagenEndOfDayUTC(endDate)
        : undefined;

      const response =
        await BusinessAccountService.postApiBusinessAccountListTopCustomersBracketsForAdmin(
          {
            from,
            to,
            mode,
          }
        );

      setBrackets(response.brackets ?? []);
      setTotalCustomers(response.totalCustomers ?? 0);
      setError(null);
    } catch (err) {
      setError("Failed to fetch customer brackets");
      console.error("Error fetching customer brackets:", err);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [startDate, endDate, mode]);

  useEffect(() => {
    fetchBrackets();
  }, [fetchBrackets]);

  const handleQuickFilter = (filterType: string) => {
    setSelectedQuickFilter(filterType);

    switch (filterType) {
      case "current":
        setStartDate("");
        setEndDate("");
        setMode(BalanceBracketMode.ALL);
        break;
      case "asOfDate":
        // Net balance as of currentAsOfDate. Backend handles this via the
        // existing `to` parameter: only `to` set + Mode=ALL filters
        // transactions strictly before end-of-day(date) — the snapshot.
        setStartDate("");
        setEndDate(currentAsOfDate);
        setMode(BalanceBracketMode.ALL);
        break;
      case "thisMonth": {
        const range = getDateRangeForThisMonth();
        setStartDate(range.start);
        setEndDate(range.end);
        setMode(BalanceBracketMode.ALL_EARNED_POINTS);
        break;
      }
      case "previousMonth": {
        const range = getDateRangeForPreviousMonth();
        setStartDate(range.start);
        setEndDate(range.end);
        setMode(BalanceBracketMode.ALL_EARNED_POINTS);
        break;
      }
      case "lastThreeMonths": {
        const range = getDateRangeForLastThreeMonths();
        setStartDate(range.start);
        setEndDate(range.end);
        setMode(BalanceBracketMode.ALL_EARNED_POINTS);
        break;
      }
      case "allTime":
        setStartDate("");
        setEndDate("");
        setMode(BalanceBracketMode.ALL_EARNED_POINTS);
        break;
    }
  };

  const handleDateChange = (newDate: string) => {
    // Picking a date is itself an explicit filter intent — activate asOfDate.
    setCurrentAsOfDate(newDate);
    setSelectedQuickFilter("asOfDate");
    setStartDate("");
    setEndDate(newDate);
    setMode(BalanceBracketMode.ALL);
  };

  const handleDatePickerFocus = () => {
    if (selectedQuickFilter !== "asOfDate") {
      handleQuickFilter("asOfDate");
    }
  };

  if (initialLoad) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  const chartData = brackets.map((b) => ({
    label: formatBracketLabel(b),
    customerCount: b.customerCount ?? 0,
  }));

  return (
    <div>
      <div className="flex flex-col gap-4 mb-10">
        <h2 className="font-medium">Customer Points Brackets</h2>

        {SHOW_MODE_FILTER && (
          <div className="flex items-center gap-4 mb-4">
            <div className="w-[250px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mode
              </label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md"
                value={mode}
                onChange={(e) => setMode(e.target.value as BalanceBracketMode)}
              >
                {Object.values(BalanceBracketMode).map((m) => (
                  <option key={m} value={m}>
                    {MODE_LABELS[m]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="flex gap-4">
          <div className="bg-white rounded-lg shadow p-4 min-w-[200px] w-fit">
            <div className="text-gray-500 text-sm">Total Customers</div>
            <div className="text-2xl font-semibold mt-1">
              {formatInteger(totalCustomers)}
            </div>
          </div>
        </div>
      </div>

      {loading && !initialLoad && (
        <div className="flex justify-center my-8">
          <Loader />
        </div>
      )}

      {error && <div className="error mb-4">{error}</div>}

      {/* Quick Filter Buttons — control bracket dataset of the chart below */}
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <Button
          onClick={() => handleQuickFilter("current")}
          variant={selectedQuickFilter === "current" ? "primary" : "outline"}
          size="sm"
        >
          Current
        </Button>
        <input
          aria-label="Net balance as of date"
          type="date"
          className={`p-1.5 rounded-md text-sm border transition-colors ${
            selectedQuickFilter === "asOfDate"
              ? "border-primary ring-2 ring-primary/30"
              : "border-gray-300"
          }`}
          value={currentAsOfDate}
          onFocus={handleDatePickerFocus}
          onChange={(e) => handleDateChange(e.target.value)}
        />
        <Button
          onClick={() => handleQuickFilter("thisMonth")}
          variant={selectedQuickFilter === "thisMonth" ? "primary" : "outline"}
          size="sm"
        >
          This Month
        </Button>
        <Button
          onClick={() => handleQuickFilter("previousMonth")}
          variant={selectedQuickFilter === "previousMonth" ? "primary" : "outline"}
          size="sm"
        >
          Previous Month
        </Button>
        <Button
          onClick={() => handleQuickFilter("lastThreeMonths")}
          variant={selectedQuickFilter === "lastThreeMonths" ? "primary" : "outline"}
          size="sm"
        >
          Last 3 Months
        </Button>
        <Button
          onClick={() => handleQuickFilter("allTime")}
          variant={selectedQuickFilter === "allTime" ? "primary" : "outline"}
          size="sm"
        >
          All Time
        </Button>
      </div>

      {!loading && brackets.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4">
            {mode === BalanceBracketMode.ALL
              ? "Customers by Net Balance"
              : "Customers by Earned Points"}
          </h3>
          <div className="h-[500px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={120}
                  tick={{ fontSize: 13 }}
                />
                <Tooltip
                  formatter={(value: number) => [
                    formatInteger(value),
                    "Customers",
                  ]}
                />
                <Bar dataKey="customerCount" radius={[0, 4, 4, 0]}>
                  {chartData.map((_entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={BAR_COLORS[index % BAR_COLORS.length]}
                    />
                  ))}
                  <LabelList
                    dataKey="customerCount"
                    position="insideRight"
                    style={{ fontSize: 13, fill: "#fff", fontWeight: 600 }}
                    formatter={(value: number) => formatInteger(value)}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminCustomerBrackets;
